import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { getProject, projects } from "@/content/projects";
import { breadcrumbScript, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/projects/$slug")({
  loader: ({ params }) => {
    const project = getProject(params.slug);
    if (!project) throw notFound();
    return project;
  },
  head: ({ loaderData }) => {
    const url = `${SITE_URL}/projects/${loaderData.slug}`;
    return {
      meta: [
        { title: `${loaderData.title} | Phil Carr` },
        { name: "description", content: loaderData.description },
        { property: "og:title", content: `${loaderData.title} | Phil Carr` },
        { property: "og:description", content: loaderData.description },
        { property: "og:url", content: url },
        { property: "og:image", content: `${SITE_URL}${loaderData.image}` },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbScript([
          { name: "Home", url: `${SITE_URL}/` },
          { name: "Projects", url: `${SITE_URL}/projects` },
          { name: loaderData.shortTitle, url },
        ]),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: loaderData.title,
            description: loaderData.description,
            url,
            image: `${SITE_URL}${loaderData.image}`,
            creator: { "@id": `${SITE_URL}/#person` },
            about: loaderData.services,
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Project not found</h1>
    </div>
  ),
  component: ProjectPage,
});

function ProjectPage() {
  const project = Route.useLoaderData();
  const otherProjects = projects.filter((item) => item.slug !== project.slug);

  return (
    <>
      <PageHero title={project.title} backgroundImage={project.image} />
      <article className="inner-content-section container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
        <div className="grid gap-10 lg:grid-cols-[1fr_20rem] lg:items-start">
          <div>
            <p className="demo-section-label">{project.sector}</p>
            <h2 className="site-heading-md mt-4 font-display font-light">
              Project overview
            </h2>
            <div className="site-copy mt-6 space-y-5 text-muted-foreground">
              {project.summary.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>

            <figure className="demo-image-frame mt-10 overflow-hidden">
              <img
                src={project.image}
                alt={`${project.client} website case study`}
                width={1200}
                height={675}
                loading="eager"
                decoding="async"
                className="h-auto w-full object-cover"
              />
            </figure>

            <section className="mt-14">
              <h2 className="site-heading-md font-display font-light">Challenge</h2>
              <p className="site-copy mt-5 text-muted-foreground">{project.challenge}</p>
            </section>

            <section className="mt-14">
              <h2 className="site-heading-md font-display font-light">Approach</h2>
              <ul className="mt-6 grid gap-4 pl-0">
                {project.approach.map((item) => (
                  <li key={item} className="demo-card p-5 text-muted-foreground">
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-14">
              <h2 className="site-heading-md font-display font-light">Result</h2>
              <div className="site-copy mt-5 space-y-5 text-muted-foreground">
                {project.result.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          </div>

          <aside className="demo-card sticky top-28 p-6">
            <p className="demo-section-label">Project details</p>
            <dl className="mt-6 space-y-5 text-sm">
              <div>
                <dt className="font-semibold text-foreground">Client</dt>
                <dd className="mt-1 text-muted-foreground">{project.client}</dd>
              </div>
              <div>
                <dt className="font-semibold text-foreground">Services</dt>
                <dd className="mt-2 flex flex-wrap gap-2">
                  {project.services.map((service) => (
                    <span
                      key={service}
                      className="rounded-full border border-border px-3 py-1 text-muted-foreground"
                    >
                      {service}
                    </span>
                  ))}
                </dd>
              </div>
            </dl>
            <a
              href={project.externalUrl}
              className="demo-link-arrow-button mt-8"
              rel="noreferrer"
              target="_blank"
            >
              Visit live site
              <span className="demo-arrow-circle">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </a>
          </aside>
        </div>
      </article>

      {otherProjects.length > 0 && (
        <section className="border-t border-border bg-card/60">
          <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-20">
            <p className="demo-section-label">More proof</p>
            <h2 className="site-heading-md mt-4 font-display font-light">Related projects</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {otherProjects.map((item) => (
                <Link
                  key={item.slug}
                  to="/projects/$slug"
                  params={{ slug: item.slug }}
                  className="demo-card demo-card-hover p-6 text-decoration-none"
                >
                  <h3 className="font-display text-2xl">{item.shortTitle}</h3>
                  <p className="mt-3 text-sm text-muted-foreground">{item.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
